/* eslint-disable max-lines-per-function */
import { checkSuccessResponse } from '@ad/ad_utils/src/utils/common';
import {
  Button,
  message,
  Table,
  Popover,
  Dropdown,
  Input,
  Loading,
  Empty,
  ITableColumnProps,
  ITableRowSelection,
  Link
} from '@byte-design/ui';
import React, { useState, useMemo, useEffect } from 'react';
import style from './index.module.scss';

import { useTranslation } from 'react-i18next';
import { useLoadingEffect } from '../../../../../utils/hooks/loading';
import TTCXModal from '../../TTCXModal';
import { observer } from 'mobx-react';
import { usePersistCallback, useClickOutside } from '@byted/hooks';
import isArray from 'lodash/isArray';
import uniqWith from 'lodash/uniqWith';
import uniq from 'lodash/uniq';
import type { MAxiosResponse } from '@ad/ad_utils/src/utils/mAxios';
import AssetPlayer, { AssetInfo, AssetTypeEnum } from '../../../../AssetPlayer';
import advIdHelpScreenshotImg from '../../../../../assets/images/ad-manager-advid.png';
import AuthManageConfirmModal from '../AuthManageConfirmModal';
import { AdvAssetsAuthResult } from '../AuthManageConfirmModal/type';
import cx from 'classnames';

const advIdHelpAssets: AssetInfo[] = [
  {
    type: AssetTypeEnum.IMAGE,
    info: {
      url: advIdHelpScreenshotImg
    }
  }
];

export interface AdvInfo {
  id: string;
  name: string;
}

export const advAssetsPermissionStarlingKey = {
  2: 'apply_permission_level_no_access',
  3: 'apply_permission_level_full_access'
};

function Send2AdsManager(props: {
  advDatasource: AdvInfo[];
  canManuallyAddAdv?: boolean;
  sendVideoToTtam?: (req: {
    fileIds: string[];
    advIds: string[];
    shareToken: string;
    permissionType?: AdvAssetsAuthResult;
  }) => Promise<MAxiosResponse<any>>;
  shareToken?: string;
  selectedVideos?: { fileId: string }[];
  currentOperateFileId?: string;
  show: boolean;
  onClose: () => void;
  sendCustomEvent?: Function;
  source?: string;
  onSearchAdv?: (advId: string) => Promise<AdvInfo[] | undefined>;
  onAddAdv?: (newAdv: AdvInfo) => void;
  onClickSync?: (advs: { id: string; name: string }[]) => Promise<void>;
  additionalAdvKeyInSessionStorage?: string;
  saveAdditionalAdvToSessionStorage?: boolean;
  hasPermissionEditEntry?: boolean;
}) {
  const {
    sendVideoToTtam,
    shareToken = '',
    selectedVideos = [],
    currentOperateFileId = '',
    advDatasource,
    sendCustomEvent,
    show,
    onClose,
    source,
    canManuallyAddAdv = false,
    onSearchAdv,
    onAddAdv,
    onClickSync,
    additionalAdvKeyInSessionStorage = 'ttcx_manually_added_adv_info',
    saveAdditionalAdvToSessionStorage = true,
    hasPermissionEditEntry = true
  } = props;
  const [selectedAdvAccount, setSelectedAdvAccount] = useState<string[]>([]);
  const [dropdownWidth, setDropdownWidth] = useState<string | number>('100%');
  const [showAdvSearchDropdown, setShowAdvSearchDropdown] = useState(false);
  const [showAdvIdHelpScreenshot, setShowAdvIdHelpScreenshot] = useState(false);
  const [advIdToSearch, setAdvIdToSearch] = useState('');
  const [advSearchRes, setAdvSearchRes] = useState<AdvInfo[] | undefined>();
  const [additionalAdvs, setAdditionalAdvs] = useState<AdvInfo[]>(() => {
    const prevAdvsJsonStr = sessionStorage.getItem(additionalAdvKeyInSessionStorage) || '';
    try {
      const prevAdvsJson = JSON.parse(prevAdvsJsonStr);
      if (isArray(prevAdvsJson)) {
        return prevAdvsJson.filter(
          (item) => item && typeof item.id === 'string' && typeof item.name === 'string'
        ) as AdvInfo[];
      }
      return [];
    } catch {
      return [];
    }
  });

  const advSearchInputRef = useClickOutside<HTMLDivElement>(undefined, () => setShowAdvSearchDropdown(false));

  const { t } = useTranslation();

  const finalDataSource = useMemo(() => {
    const authedAdvList = advDatasource.map((item) => ({ ...item, id: String(item.id) }));
    return [
      ...additionalAdvs.filter((item) => !authedAdvList.some((adv) => adv.id === item.id)),
      ...authedAdvList
    ];
  }, [advDatasource, additionalAdvs]);

  const [confirming, onConfirmSend] = useLoadingEffect(async () => {
    const advs = finalDataSource.filter((item) => selectedAdvAccount.includes(item.id));
    if (typeof onClickSync === 'function') {
      await onClickSync(advs);
    }

    const fileIds = currentOperateFileId ? [currentOperateFileId] : selectedVideos.map((item) => item.fileId);
    sendCustomEvent?.('asset_management_sync_videos_modal_click', {
      advCount: selectedAdvAccount.length,
      fileCount: fileIds.length,
      source,
      shareToken
    });
    if (typeof sendVideoToTtam === 'function') {
      const res = await sendVideoToTtam({
        fileIds,
        advIds: selectedAdvAccount,
        shareToken,
        permissionType: advAssetDataPermissionLevel
      });
      if (checkSuccessResponse(res.code)) {
        message.success({
          content: t('success_sync_to_ads_manager', 'Sync to ads manager successfully')
        });
        onClose();
      }
    }
  });

  const advColumns = useMemo<ITableColumnProps<{ id: string; name: string }>[]>(
    () => [
      {
        key: 'name',
        width: 120,
        title: t('ads_list_adv_account_name_title', 'Ads account name'),
        dataIndex: 'name'
      },
      {
        key: 'id',
        width: 120,
        title: t('ads_list_adv_account_id_title', 'Ads Account ID'),
        dataIndex: 'id'
      }
    ],
    [t]
  );

  const tableRowSelection = useMemo<ITableRowSelection<AdvInfo, 'id'>>(
    () => ({
      type: 'checkbox',
      onChange: setSelectedAdvAccount,
      selectedRowKeys: selectedAdvAccount
    }),
    [selectedAdvAccount]
  );

  const [searchingAdv, searchAdv] = useLoadingEffect(async () => {
    setShowAdvSearchDropdown(true);
    if (typeof onSearchAdv !== 'function') {
      return;
    }
    const res = await onSearchAdv(advIdToSearch);
    setAdvSearchRes(res?.map((item) => ({ ...item, id: String(item.id) })));
  });

  const onClickAddAdv = usePersistCallback((adv: AdvInfo) => {
    if (!adv) {
      return;
    }
    setShowAdvSearchDropdown(false);
    setAdditionalAdvs((prevAdditionalAdvs) => {
      const res = uniqWith([...prevAdditionalAdvs, adv], (a, b) => a.id === b.id);
      if (saveAdditionalAdvToSessionStorage) {
        sessionStorage.setItem(additionalAdvKeyInSessionStorage, JSON.stringify(res));
      }
      return res;
    });
    setSelectedAdvAccount((prevSelectedAdvIds) => uniq([adv.id, ...prevSelectedAdvIds]));
    // clear search input
    setAdvIdToSearch('');
    if (typeof onAddAdv === 'function') {
      onAddAdv(adv);
    }
  });

  const openAdvIdHelpScreenshot = usePersistCallback(() => setShowAdvIdHelpScreenshot(true));
  const closeAdvIdHelpScreenshot = usePersistCallback(() => setShowAdvIdHelpScreenshot(false));

  const advSearchDropdown = useMemo(() => {
    if (searchingAdv) {
      return (
        <Loading.BlockLoading show>
          <div style={{ height: 100 }} />
        </Loading.BlockLoading>
      );
    }
    if (!advSearchRes?.length) {
      return <Empty content={t('adv_search_result_empty_hint', 'Ad account ID invaild')} />;
    }
    return (
      <>
        <div className={style.advConfirmHint}>
          {t('adv_search_results_hint', 'Confirm your ad account name and ID')}
        </div>
        <Dropdown.Menu>
          {advSearchRes?.map((item) => (
            <Dropdown.MenuItem key={item.id} id={item.id}>
              <div className={style.advDropdownItem}>
                <div className={style.advDropdownInfo}>
                  <div className={style.advDropdownName}>{item.name}</div>
                  <div className={style.advDropdownId}>{`ID: ${item.id}`}</div>
                </div>
                <Button onClick={() => onClickAddAdv(item)}>{t('add_on_add_add_on', 'Add')}</Button>
              </div>
            </Dropdown.MenuItem>
          ))}
        </Dropdown.Menu>
      </>
    );
  }, [searchingAdv, advSearchRes, t]);

  useEffect(() => {
    if (show && finalDataSource.length > 0) {
      setSelectedAdvAccount([finalDataSource[0].id]);
    }
  }, [show]);

  useEffect(() => {
    // measure search bar width
    if (!canManuallyAddAdv || !advSearchInputRef.current) {
      return;
    }
    setDropdownWidth(advSearchInputRef.current.clientWidth);
  }, [canManuallyAddAdv]);

  useEffect(() => {
    if (!show) {
      setShowAdvSearchDropdown(false);
      setAdvAssetDataPermissionLevel(AdvAssetsAuthResult.AUTHORISE);
    }
  }, [show]);

  const [advAssetDataPermissionLevel, setAdvAssetDataPermissionLevel] = useState<AdvAssetsAuthResult>(
    AdvAssetsAuthResult.AUTHORISE
  );
  const [showAdvAssetDataPermissionModal, setShowAdvAssetDataPermissionModal] = useState(false);
  const AdvAssetDataPermissionEntry = (
    <div className={style.permissionManage}>
      <span className={style.permissionDesc}>
        {t('select_ads_account_modal_bottom_label', 'The creative partner will have ', {
          permission: t(advAssetsPermissionStarlingKey[advAssetDataPermissionLevel])
        })}
        &nbsp;
        <span className={style.permissionEdit} onClick={() => setShowAdvAssetDataPermissionModal(true)}>
          {t('permission_edit')}
        </span>
      </span>
    </div>
  );

  return (
    <>
      <TTCXModal
        wrapperClassName={style.syncModal}
        size="md"
        show={show}
        header={t('select_ads_account_modal_title', 'Select Ad Account')}
        onCancel={onClose}
        closable
        footer={
          <div className={cx(style.customBottom, hasPermissionEditEntry ? style.customBottomBetween : null)}>
            {hasPermissionEditEntry && AdvAssetDataPermissionEntry}
            <Button
              type="primary"
              disabled={selectedAdvAccount.length === 0}
              onClick={onConfirmSend}
              loading={confirming}
              buttonDisabled={false}
            >
              {t('sync_text', 'Sync')}
            </Button>
          </div>
        }
        content={
          <>
            {canManuallyAddAdv && (
              <>
                <Popover
                  show={showAdvSearchDropdown}
                  trigger="manual"
                  placement="bottom"
                  getPopper={advSearchDropdown}
                  getPopupContainer={() => advSearchInputRef.current}
                  className={style.advSearchInputPopover}
                  popoverStyle={{ width: dropdownWidth }}
                >
                  <div ref={advSearchInputRef} className={style.advSearchInputGroup}>
                    <Input.Group>
                      <Input
                        value={advIdToSearch}
                        onValueChange={setAdvIdToSearch}
                        placeholder={t('search_adv_placeholder', 'Search and add an valid ADV ID')}
                        onPressEnter={searchAdv}
                      />
                      <Button
                        type="primary"
                        onClick={searchAdv}
                        disabled={!advIdToSearch}
                        className={style.blackBtn}
                      >
                        {t('search_adv_btn', 'Search')}
                      </Button>
                    </Input.Group>
                  </div>
                </Popover>
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <Link onClick={openAdvIdHelpScreenshot} className={style.advIdHint}>
                  {t('find_adv_id_hint', 'How can I find my adv ID?')}
                </Link>
              </>
            )}
            <Table
              columns={advColumns}
              rowSelection={tableRowSelection}
              datasource={finalDataSource}
              rowKey="id"
              emptyText={t('no_data_hint', 'No Data')}
            />
          </>
        }
      />
      <AssetPlayer
        show={showAdvIdHelpScreenshot}
        assets={advIdHelpAssets}
        onClose={closeAdvIdHelpScreenshot}
      />

      {/* asset permission management modal */}
      {showAdvAssetDataPermissionModal && (
        <AuthManageConfirmModal
          initAuthResult={advAssetDataPermissionLevel}
          show
          onCancel={() => setShowAdvAssetDataPermissionModal(false)}
          onConfirm={(value) => {
            setAdvAssetDataPermissionLevel(value);
            setShowAdvAssetDataPermissionModal(false);
          }}
        />
      )}
    </>
  );
}

export default observer(Send2AdsManager);
